/** Arcade rewards use only active simulation time and already-earned contacts.
 * Every extra point is local to this run; there is no spendable currency. */
export const BOB_ARCADE = Object.freeze({
  windowMs: 4000,
  maxMultiplier: 4,
  bonusBaseCap: 150,
  reactionRadius: 2.8,
  reactionReach: 7,
  reactionLimit: 6,
});

/** Fresh finite chain state, captured together with rewards at checkpoints. */
export function createBobArcade() {
  return {
    chain: 0,
    bestChain: 0,
    multiplier: 1,
    expiresAt: 0,
    lastKind: null,
    earned: 0,
    reactions: 0,
    springs: [],
  };
}

/** Break only the current chain on damage or expiry; banked bonus points and
 * best chain survive. Input is an owned session clone, time is active ms. */
export function resetBobArcade(arcade) {
  arcade.chain = 0;
  arcade.multiplier = 1;
  arcade.expiresAt = 0;
  arcade.lastKind = null;
}

/** Convert a unique simulation award into bounded arcade feedback. Mutates only
 * the owned chain state and returns the extra awarded points, never base points.
 * Food/crates are consumed by domain68; timing requires new forward progress.
 * Repeat spring contacts cannot extend a chain. Null means no reward action. */
export function rewardBobArcade(arcade, elapsed, type, cue) {
  if (!Number.isFinite(elapsed) || elapsed < 0) throw new RangeError('Invalid arcade time.');
  const qualifying = ['food', 'crate', 'checkpoint', 'bonus-route'].includes(type);
  const timing = type === 'timing' && cue.points > 0;
  const spring = type === 'spring' && !arcade.springs.includes(cue.objectId);
  if (!qualifying && !timing && !spring) return null;
  if (spring) arcade.springs.push(cue.objectId);
  if (arcade.expiresAt <= elapsed) resetBobArcade(arcade);
  const previousMultiplier = arcade.multiplier;
  arcade.chain++;
  arcade.bestChain = Math.max(arcade.bestChain, arcade.chain);
  arcade.multiplier = Math.min(BOB_ARCADE.maxMultiplier, 1 + Math.floor(arcade.chain / 4));
  arcade.expiresAt = elapsed + BOB_ARCADE.windowMs;
  arcade.lastKind = type;
  const points = Math.min(BOB_ARCADE.bonusBaseCap, cue.points || 0) * (arcade.multiplier - 1);
  arcade.earned += points;
  return {
    points,
    chain: arcade.chain,
    multiplier: arcade.multiplier,
    tierChanged: arcade.multiplier > previousMultiplier,
    expiresAt: arcade.expiresAt,
    sourceType: type,
    label: ['NICE CHAIN', 'SUPER CHAIN', 'MEGA CHAIN', 'BOB FEVER'][arcade.multiplier - 1],
  };
}

/** A smashed crate triggers a visible, spatial food cascade, in metres. Each
 * frontier reaches only nearby unconsumed pickups; total reach and six-node
 * budget bound work and rewards. Crates/gates cannot be cleared remotely.
 * Returns ordered real food plus its causal origin without mutating geometry. */
export function bobReactionFood(course, origin, collected) {
  const visited = new Set(collected);
  const frontier = [origin];
  const result = [];
  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
  while (frontier.length && result.length < BOB_ARCADE.reactionLimit) {
    const from = frontier.shift();
    const nearby = course.food
      .filter(
        (food) =>
          !visited.has(food.id) &&
          distance(from, food) <= BOB_ARCADE.reactionRadius &&
          distance(origin, food) <= BOB_ARCADE.reactionReach,
      )
      .sort((a, b) => distance(from, a) - distance(from, b) || a.id.localeCompare(b.id));
    for (const food of nearby) {
      if (result.length === BOB_ARCADE.reactionLimit) break;
      visited.add(food.id);
      result.push({ food, from: { x: from.x, y: from.y, z: from.z } });
      frontier.push(food);
    }
  }
  return result;
}
